/**
 * An inline message. NFR-05.
 *
 * `role="status"` for information and `role="alert"` for problems, so a screen
 * reader announces a storage failure rather than leaving it as text nobody
 * sees. That distinction is the whole reason this is a component rather than a
 * styled paragraph.
 */
import type { ReactNode } from 'react'

export interface NoticeProps {
  tone: 'info' | 'warning' | 'error'
  children: ReactNode
  action?: { label: string; onClick: () => void }
}

const TONE = {
  info: 'border-accent/25 bg-accent/5 text-ink',
  warning: 'border-flow-spotting bg-flow-spotting/10 text-ink',
  error: 'border-flow-heavy bg-flow-heavy/10 text-ink',
} as const

export function Notice({ tone, children, action }: NoticeProps) {
  return (
    <div
      role={tone === 'info' ? 'status' : 'alert'}
      className={`flex flex-wrap items-center gap-2 rounded-lg border p-3 text-sm transition-all duration-300 ${TONE[tone]}`}
    >
      <span className="flex-1">{children}</span>
      {action ? (
        <button
          type="button"
          onClick={action.onClick}
          className="min-h-11 rounded-lg border border-current px-3 text-xs transition-all duration-200 hover:bg-current/10 active:scale-95"
        >
          {action.label}
        </button>
      ) : null}
    </div>
  )
}
